import React, { useState, useEffect } from 'react';
import { Users, UserCircle, Shield, Clock, Mail, Building2, Phone, Search, Filter, RefreshCw, Plus, Edit, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';
import UserFormModal from '../components/UserFormModal';
import UserIndustryManager from '../components/UserIndustryManager';
import ClientAssignmentModal from '../components/ClientAssignmentModal';
import { getAllRoles, getRoleLabel, getRoleColorClasses } from '../utils/roleConfig';
import { getUserRoles, isTeamLead, canHaveIndustries } from '../utils/roleUtils';
import { sortUsersByName } from '../utils/userUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showIndustryManager, setShowIndustryManager] = useState(false);
  const [selectedUserForIndustry, setSelectedUserForIndustry] = useState(null);
  const [showClientAssignment, setShowClientAssignment] = useState(false);
  const [selectedUserForClients, setSelectedUserForClients] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token') || 'demo-token'}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      const sortedUsers = sortUsersByName(data.users || []);
      setUsers(sortedUsers);
      toast.success(`Loaded ${data.total} users`);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      // Set demo data for development
      setUsers([
        {
          id: '1',
          username: 'junoadmin',
          email: 'admin@junotalent.com',
          full_name: 'Juno Administrator',
          role: 'junoadmin',
          is_active: true,
          is_local_user: true,
          created_at: new Date().toISOString(),
          last_login: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleModalClose = () => {
    setShowUserModal(false);
    setEditingUser(null);
  };

  const handleUserSuccess = () => {
    setShowUserModal(false);
    setEditingUser(null);
    fetchUsers();
  };


  // Use centralized role utilities
  const getRoleBadgeColor = (role) => {
    return getRoleColorClasses(role) + ' border';
  };

  const getRoleIcon = (role) => {
    const adminRoles = ['junoadmin', 'admin'];
    const leadRoles = ['lead', 'lead_recruiter', 'recruiter_supervisor'];
    const accountRoles = ['account_manager', 'account_exec', 'Account Manager'];
    
    if (adminRoles.includes(role)) return Shield;
    if (leadRoles.includes(role)) return UserCircle;
    if (accountRoles.includes(role)) return Briefcase;
    return Users;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Handle role filters with multiple role name variations
    const matchesRole = roleFilter === 'all' || 
                       user.role === roleFilter ||
                       (roleFilter === 'admin' && (user.role === 'admin' || user.role === 'junoadmin')) ||
                       (roleFilter === 'lead' && (user.role === 'lead' || user.role === 'lead_recruiter')) ||
                       (roleFilter === 'account_manager' && (user.role === 'account_manager' || user.role === 'Account Manager')) ||
                       (roleFilter === 'account_exec' && user.role === 'account_exec');
    
    const matchesUserType = 
      userTypeFilter === 'all' ||
      (userTypeFilter === 'local' && user.is_local_user) ||
      (userTypeFilter === 'microsoft' && !user.is_local_user);
    
    return matchesSearch && matchesRole && matchesUserType;
  });

  // Get available roles from centralized config
  const availableRoles = getAllRoles();
  const roleOptions = ['all', ...availableRoles.map(r => r.value)];
  const userTypeCounts = {
    total: users.length,
    local: users.filter(u => u.is_local_user).length,
    microsoft: users.filter(u => !u.is_local_user).length,
    active: users.filter(u => u.is_active).length
  };

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter, userTypeFilter, searchTerm]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-600" />
            User Management
          </h1>
          <p className="text-gray-600 mt-1">Manage system users and their roles</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowUserModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add User
          </button>
          <button
            onClick={fetchUsers}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Role Toggle Bar */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Filter by Role</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setRoleFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  roleFilter === 'all'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Users
              </button>
              <button
                onClick={() => setRoleFilter('recruiter')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  roleFilter === 'recruiter'
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Recruiters
              </button>
              <button
                onClick={() => setRoleFilter('lead')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  roleFilter === 'lead'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Lead Recruiters
              </button>
              <button
                onClick={() => setRoleFilter('recruiter_supervisor')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  roleFilter === 'recruiter_supervisor'
                    ? 'bg-blue-700 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Recruiter Supervisors
              </button>
              <button
                onClick={() => setRoleFilter('account_manager')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  roleFilter === 'account_manager'
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Account Managers
              </button>
              <button
                onClick={() => setRoleFilter('account_exec')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  roleFilter === 'account_exec'
                    ? 'bg-orange-700 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Account Execs
              </button>
              <button
                onClick={() => setRoleFilter('admin')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  roleFilter === 'admin' || roleFilter === 'junoadmin'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Admins
              </button>
            </div>
          </div>

          {/* User Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Filter by Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => setUserTypeFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  userTypeFilter === 'all'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Types
              </button>
              <button
                onClick={() => setUserTypeFilter('local')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  userTypeFilter === 'local'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Local Users
              </button>
              <button
                onClick={() => setUserTypeFilter('microsoft')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  userTypeFilter === 'microsoft'
                    ? 'bg-green-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Microsoft Users
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center space-x-2 text-gray-500">
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      <span>Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No users found</p>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => {
                  const RoleIcon = getRoleIcon(user.role);
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {user.full_name?.charAt(0) || user.username?.charAt(0) || 'U'}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name || user.username}
                            </div>
                            <div className="text-sm text-gray-500">@{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          {(user.roles || [user.role]).map((role, idx) => {
                            const RoleIconForBadge = getRoleIcon(role);
                            return (
                              <span 
                                key={idx} 
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(role)}`}
                              >
                                <RoleIconForBadge className="h-3.5 w-3.5" />
                                {getRoleLabel(role)}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.is_local_user 
                            ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                            : 'bg-green-100 text-green-800 border border-green-300'
                        }`}>
                          {user.is_local_user ? (
                            <>
                              <Shield className="h-3.5 w-3.5" />
                              Local
                            </>
                          ) : (
                            <>
                              <Building2 className="h-3.5 w-3.5" />
                              Microsoft
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          {user.email || user.microsoft_email || 'N/A'}
                        </div>
                        {user.phone && (
                          <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                            <Phone className="h-4 w-4 text-gray-400" />
                            {user.phone}
                          </div>
                        )}
                        {user.industry && canHaveIndustries(user.role) && (
                          <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            {user.industry}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="h-4 w-4 text-gray-400" />
                          {formatDate(user.last_login)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Industries button for recruiters, account managers, and leads */}
                          {['recruiter', 'lead', 'lead_recruiter', 'recruiter_supervisor', 'account_manager', 'account_exec', 'Account Manager'].includes(user.role) && (
                            <button
                              onClick={() => {
                                setSelectedUserForIndustry(user);
                                setShowIndustryManager(true);
                              }}
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="Manage Industries"
                              data-testid={`manage-industries-${user.id}`}
                            >
                              <Briefcase className="h-4 w-4" />
                            </button>
                          )}
                          {/* Clients button for lead recruiters and account managers */}
                          {['lead_recruiter', 'recruiter_supervisor', 'account_manager'].includes(user.role) && (
                            <button
                              onClick={() => {
                                setSelectedUserForClients(user);
                                setShowClientAssignment(true);
                              }}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Manage Clients"
                              data-testid={`manage-clients-${user.id}`}
                            >
                              <Building2 className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit User"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && filteredUsers.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Results info */}
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, filteredUsers.length)}</span> of{' '}
              <span className="font-medium">{filteredUsers.length}</span> results
            </div>

            {/* Page size selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700">Per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Pagination controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 border rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="px-2 text-gray-500">...</span>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      {showUserModal && (
        <UserFormModal
          user={editingUser}
          onClose={handleModalClose}
          onSuccess={handleUserSuccess}
          backendUrl={BACKEND_URL}
        />
      )}
      
      {/* User Industry Manager Modal */}
      {showIndustryManager && selectedUserForIndustry && (
        <UserIndustryManager
          user={selectedUserForIndustry}
          onClose={() => {
            setShowIndustryManager(false);
            setSelectedUserForIndustry(null);
          }}
          onUpdate={() => {
            fetchUsers(); // Refresh users list
            setShowIndustryManager(false);
            setSelectedUserForIndustry(null);
          }}
        />
      )}

      {/* Client Assignment Modal */}
      {showClientAssignment && selectedUserForClients && (
        <ClientAssignmentModal
          isOpen={showClientAssignment}
          user={selectedUserForClients}
          onClose={() => {
            setShowClientAssignment(false);
            setSelectedUserForClients(null);
          }}
          onUpdate={() => {
            fetchUsers(); // Refresh users list
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
